/**
 * iter45 (M3) — Tournament switcher dropdown for the league dashboard header.
 *
 * Behaviour:
 * - LIST: fetches `GET /api/leagues/{lid}/tournaments` on mount.
 * - DEFAULT vs VIEW: the backend tracks the league-wide DEFAULT (admin-set);
 *   each user can override their view per-league via localStorage. The
 *   override is purely client-side — doesn't affect anyone else's view.
 * - SET DEFAULT (admin only): calls `PATCH /api/leagues/{lid}/active-tournament`.
 * - "+ New Tournament" CTA (admin only): hooks into the parent — the parent
 *   wires it to navigate to /league/{lid}/setup (existing setup flow).
 *
 * Hides itself when the league has ≤1 tournament. A single-tournament league
 * doesn't need a switcher; the parent renders nothing in that case.
 *
 * Props:
 *   leagueId          string
 *   currentTid        string   tournament id currently being VIEWED
 *   isAdmin           bool     admin → can set league-default + see + New
 *   onChange          (tid) => void   parent updates view state
 *   onNewTournament   () => void      admin-only "+ New Tournament" handler
 *   refreshKey        number   bump to force a re-fetch (e.g. after a new
 *                              tournament is created)
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ChevronDown, Check, Star, Plus, Calendar, Lock, Info } from "lucide-react";


// Persist view choice per-league in localStorage. Cleared = use league default.
const viewKey = (leagueId) => `tcr_view_tournament:${leagueId}`;
export function getStoredViewTournament(leagueId) {
    try { return localStorage.getItem(viewKey(leagueId)) || null; }
    catch { return null; }
}
export function setStoredViewTournament(leagueId, tid) {
    try {
        if (tid) localStorage.setItem(viewKey(leagueId), tid);
        else localStorage.removeItem(viewKey(leagueId));
    } catch { /* ignore */ }
}


export default function TournamentSwitcher({
    leagueId, currentTid, isAdmin, onChange, onNewTournament, onCountChange, refreshKey = 0,
}) {
    const [data, setData] = useState(null);  // {tournaments, active_tournament_id, can_create_more, ...}
    const [open, setOpen] = useState(false);
    const [infoOpen, setInfoOpen] = useState(false);  // iter47c — info popup inside the dropdown
    const [setting, setSetting] = useState(false);
    const wrapRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        api.listLeagueTournaments(leagueId)
            .then((d) => {
                if (cancelled) return;
                setData(d);
                onCountChange?.((d?.tournaments || []).length);
            })
            .catch(() => { /* silent — switcher is a progressive enhancement */ });
        return () => { cancelled = true; };
    }, [leagueId, refreshKey, onCountChange]);

    // Close on outside click
    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [open]);

    const tournaments = useMemo(() => data?.tournaments || [], [data?.tournaments]);
    const activeTid = data?.active_tournament_id;
    const current = useMemo(
        () => tournaments.find(t => t.id === currentTid) || tournaments.find(t => t.id === activeTid) || tournaments[0],
        [tournaments, currentTid, activeTid],
    );

    // iter87.167 — Hide COMPLETE / FINAL tournaments from the switcher
    // by default so live rivalries with many past pools don't drown the
    // list. Toggle state persists per-league in localStorage. Empty-set
    // guard: if every tournament is complete (post-season cleanup) we
    // auto-show them all with a soft banner.
    const showCompletedKey = `tcr_switcher_show_completed:${leagueId}`;
    const [showCompleted, setShowCompleted] = useState(() => {
        try { return localStorage.getItem(showCompletedKey) === "1"; }
        catch { return false; }
    });
    // iter87.199.128 — Also treat the raw `lifecycle_state === "completed"`
    // value as done. The zombie-lock migration made `completion_authorized_by`
    // the canonical "is this truly locked" signal, but every tournament
    // completed BEFORE that migration only ever got `lifecycle_state`
    // flipped to "completed" (never backstamped with the new field) —
    // this switcher started treating those as still-active with no way
    // to hide them. `HeroStrip.jsx`'s canonical status pill (`lib/lifecycle.js`)
    // already trusts this same raw value; this mirrors that source of truth.
    const isDoneT = (t) => !!t.completion_authorized_by
        || t.lifecycle_state === "final"
        || t.lifecycle_state === "completed";
    const activeTournaments = tournaments.filter((t) => !isDoneT(t));
    const completedTournaments = tournaments.filter(isDoneT);
    const allComplete = tournaments.length > 0 && activeTournaments.length === 0;
    const visibleTournaments = (showCompleted || allComplete)
        ? tournaments
        : activeTournaments;
    const toggleShowCompleted = () => {
        setShowCompleted((v) => {
            const next = !v;
            try {
                if (next) localStorage.setItem(showCompletedKey, "1");
                else localStorage.removeItem(showCompletedKey);
            } catch { /* ignore */ }
            return next;
        });
    };

    // Visibility logic:
    // - Multi-tournament leagues (>1 tournament): always show (switching is the point)
    // - Single-tournament + admin: always show so the admin sees they CAN start
    //   another (greyed-out + tooltip when at cap, active button when capacity)
    // - Single-tournament + non-admin: hide (nothing to switch, nothing to create)
    const hasMultiple = tournaments.length > 1;
    const canCreate = isAdmin && data?.can_create_more;
    const atCap = isAdmin && data && !data.can_create_more;
    if (!hasMultiple && !isAdmin) return null;
    if (!hasMultiple && !canCreate && !atCap) return null;

    const handlePick = (tid) => {
        setStoredViewTournament(leagueId, tid === activeTid ? null : tid);
        onChange?.(tid);
        setOpen(false);
    };

    const handleSetDefault = async (tid) => {
        if (!isAdmin) return;
        if (tid === activeTid) {
            toast.message("Already the rivalry default");
            return;
        }
        setSetting(true);
        try {
            await api.updateActiveTournament(leagueId, tid);
            // Refresh so the DEFAULT pill moves to the new selection
            const fresh = await api.listLeagueTournaments(leagueId);
            setData(fresh);
            toast.success("League default tournament updated");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to update default");
        }
        setSetting(false);
    };

    return (
        <div className="relative" ref={wrapRef} data-testid="tournament-switcher">
            <button
                type="button"
                data-testid="tournament-switcher-trigger"
                onClick={() => setOpen(o => !o)}
                className="brutal-btn-ghost text-xs py-2 max-w-[280px] min-w-0"
                title={hasMultiple ? "Switch tournament view" : "Manage tournaments in this rivalry"}
            >
                <Calendar className="w-3.5 h-3.5 text-cyber shrink-0" />
                {/* iter86.95 — HeroStrip already shows the tournament name on
                    phones; the trigger goes compact (icon + count) below sm. */}
                <span className="truncate font-bold hidden sm:inline-block">
                    {current?.name || "Tournaments"}
                </span>
                <span className="pill bg-ink border-[#333] text-[9px] py-0 px-1.5 shrink-0">
                    {data?.active_count ?? tournaments.length}/{data?.cap ?? "?"}
                </span>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>

            {open && (
                <div
                    className="fixed sm:absolute left-2 sm:left-auto right-2 sm:right-0 top-[64px] sm:top-[calc(100%+6px)] z-40 brutal-card p-2 sm:w-[340px] max-w-[calc(100vw-1rem)] max-h-[480px] overflow-y-auto shadow-2xl"
                    data-testid="tournament-switcher-menu"
                >
                    <div className="flex items-center justify-between px-2 py-1.5">
                        <div className="flex items-center gap-1.5">
                            <p className="text-[10px] uppercase tracking-widest text-zinc-500">
                                Tournaments in this rivalry
                            </p>
                            {/* iter47c — Info popup explaining multi-tournament behaviour. */}
                            <button
                                type="button"
                                data-testid="tournament-switcher-info-btn"
                                onClick={(e) => { e.stopPropagation(); setInfoOpen(v => !v); }}
                                className="p-0.5 text-zinc-500 hover:text-cyber transition-colors"
                                aria-label="About tournaments in a rivalry"
                            >
                                <Info className="w-3 h-3" />
                            </button>
                        </div>
                        <span className="text-[10px] text-zinc-500">
                            {data?.tier?.toUpperCase()} · {data?.active_count || 0}/{data?.cap || 1} active
                        </span>
                    </div>

                    {/* iter87.167 — Show / hide completed tournaments
                        toggle. Hidden by default so live rivalries with
                        lots of past pools don't drown the list. Sits
                        below the header row so the count above stays
                        authoritative. Skipped when there's nothing to
                        hide (zero completed) or when EVERY tournament
                        is complete (post-season). */}
                    {completedTournaments.length > 0 && !allComplete && (
                        <div className="flex items-center justify-end px-2 pb-1.5 -mt-1">
                            <button
                                type="button"
                                data-testid="tournament-switcher-toggle-completed"
                                onClick={(e) => { e.stopPropagation(); toggleShowCompleted(); }}
                                className="pill border-[#333] text-[9px] py-0.5 px-2 text-zinc-400 hover:text-cyber hover:border-cyber/50 transition-colors flex items-center gap-1"
                            >
                                {showCompleted
                                    ? <>Hide completed</>
                                    : <>Show completed · {completedTournaments.length}</>}
                            </button>
                        </div>
                    )}
                    {allComplete && (
                        <div
                            data-testid="tournament-switcher-all-complete-banner"
                            className="text-[10px] text-zinc-500 italic px-2 pb-2 -mt-1"
                        >
                            Every tournament in this rivalry is complete.
                        </div>
                    )}

                    {infoOpen && (
                        <div
                            data-testid="tournament-switcher-info-panel"
                            className="brutal-card-light p-2.5 mx-1 mb-2 text-[11px] text-zinc-300 leading-relaxed border-cyber/40"
                        >
                            {data?.tier === "free" ? (
                                <>
                                    Free Rivalries run <strong className="text-white">one Tournament, start to finish</strong>.
                                    <span className="block mt-1.5 text-zinc-400">
                                        Once it's complete, results stay viewable for <strong className="text-white">30 days</strong> before the Rivalry auto-archives (read-only after that).{" "}
                                        <Link to="/rivalry-pro" className="text-cyber hover:underline" data-testid="tournament-switcher-pro-plus-link">
                                            Sign up for Pro+
                                        </Link>{" "}
                                        to keep this Rivalry open and run unlimited Tournaments.
                                    </span>
                                </>
                            ) : (
                                <>
                                    One league can host <strong className="text-white">multiple tournaments</strong> over time — use this switcher to create another tournament or jump between existing ones.
                                    <span className="block mt-1.5 text-zinc-400">
                                        Completed tournaments are kept indefinitely on Pro+.
                                    </span>
                                </>
                            )}
                        </div>
                    )}

                    {tournaments.length === 0 && (
                        <p className="text-xs text-zinc-500 italic px-2 py-3">No tournaments yet.</p>
                    )}

                    <ul className="space-y-1" data-testid="tournament-switcher-list">
                        {visibleTournaments.map(t => {
                            const isViewing = t.id === currentTid;
                            const isDefault = t.id === activeTid;
                            // iter87.199.128 — See `isDoneT` above: a raw
                            // `lifecycle_state === "completed"` also counts
                            // as locked for display, even pre-dating the
                            // `completion_authorized_by` stamp.
                            const isComplete = !!t.completion_authorized_by || t.lifecycle_state === "completed";
                            // iter87.160 — Treat `lifecycle_state === "final"`
                            // as visually locked too. `final` means every
                            // game played + results in — the tournament is
                            // done, even if the admin hasn't ceremonially
                            // clicked Mark Complete. Same lock pill so the
                            // switcher stops implying "still ongoing".
                            const isFinal = !isComplete && t.lifecycle_state === "final";
                            return (
                                <li
                                    key={t.id}
                                    data-testid={`tournament-switcher-row-${t.id}`}
                                    className={`brutal-card-light p-2.5 flex items-center justify-between gap-2 ${isViewing ? "border-cyber" : ""}`}
                                >
                                    <button
                                        type="button"
                                        data-testid={`tournament-switcher-pick-${t.id}`}
                                        onClick={() => handlePick(t.id)}
                                        className="flex-1 min-w-0 text-left"
                                    >
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                            <span className={`font-bold text-sm truncate ${isViewing ? "text-cyber" : "text-white"}`}>
                                                {t.name}
                                            </span>
                                            {isDefault && (
                                                <span
                                                    className="pill bg-cyber/20 border-cyber text-cyber text-[8px] py-0 px-1.5"
                                                    data-testid={`tournament-default-pill-${t.id}`}
                                                    title="Rivalry-wide default — what new visitors see"
                                                >
                                                    <Star className="w-2 h-2" /> DEFAULT
                                                </span>
                                            )}
                                            {isComplete && (
                                                <span
                                                    className="pill bg-zinc-800 border-zinc-600 text-zinc-400 text-[8px] py-0 px-1.5"
                                                    data-testid={`tournament-complete-pill-${t.id}`}
                                                >
                                                    <Lock className="w-2 h-2" /> COMPLETE
                                                </span>
                                            )}
                                            {isFinal && (
                                                <span
                                                    className="pill bg-zinc-800 border-zinc-600 text-zinc-400 text-[8px] py-0 px-1.5"
                                                    data-testid={`tournament-final-pill-${t.id}`}
                                                    title="Every game played — no more changes"
                                                >
                                                    <Lock className="w-2 h-2" /> FINAL
                                                </span>
                                            )}
                                            {isViewing && (
                                                <Check className="w-3 h-3 text-cyber shrink-0 ml-auto" />
                                            )}
                                        </div>
                                        <p className="text-[10px] text-zinc-500 mt-0.5">
                                            {/* iter63r — Friendlier label for new API-Football templates. */}
                                            {({
                                                world_cup_api: "World Cup Format",
                                                world_cup_ko_api: "World Cup KO Format",
                                                premier_league_api: "Premier League Season",
                                                la_liga_api: "La Liga Season",
                                                champions_league_api: "Champions League",
                                                club_season_api: "Club Season",
                                            }[t.template] || (t.template || "").replace(/_/g, " "))} · stage: {t.current_stage || "setup"}
                                        </p>
                                    </button>
                                    {isAdmin && !isDefault && (
                                        <button
                                            type="button"
                                            data-testid={`tournament-set-default-${t.id}`}
                                            onClick={() => handleSetDefault(t.id)}
                                            disabled={setting}
                                            className="brutal-btn-ghost text-[10px] py-1 px-2 shrink-0 disabled:opacity-50"
                                            title="Set as the rivalry-wide default"
                                        >
                                            <Star className="w-2.5 h-2.5" /> Set default
                                        </button>
                                    )}
                                </li>
                            );
                        })}
                    </ul>

                    {(canCreate || atCap) && (
                        <button
                            type="button"
                            data-testid="tournament-switcher-new-btn"
                            onClick={() => { if (canCreate) { setOpen(false); onNewTournament?.(); } }}
                            disabled={!canCreate}
                            title={canCreate
                                ? "Start a new tournament in this rivalry"
                                : `${data?.tier?.toUpperCase()} tier is capped at ${data?.cap} active tournament${data?.cap === 1 ? "" : "s"}. Complete one or upgrade to add another.`
                            }
                            className={`w-full mt-2 text-xs py-2 ${canCreate ? "brutal-btn-primary" : "brutal-btn-ghost opacity-60 cursor-not-allowed"}`}
                        >
                            <Plus className="w-3.5 h-3.5" /> New Tournament
                        </button>
                    )}
                    {atCap && (
                        <p
                            data-testid="tournament-switcher-cap-hint"
                            className="text-[10px] text-zinc-500 italic px-2 py-2 mt-1 border-t-2 border-[#222] leading-relaxed"
                        >
                            <strong className="text-zinc-400">{data?.tier?.toUpperCase()} tier</strong> · {data?.active_count}/{data?.cap} active.
                            Complete a tournament or upgrade to start another.
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}
